import React from "react";
import {AbsoluteFill, useCurrentFrame, random} from "remotion";
import {Noise} from "../dimus/Noise";
import {Vignette} from "../dimus/Vignette";
import {FilmGrain} from "../dimus/FilmGrain";

/**
 * SceneBackground — Variable background system per scene.
 * Solves the "same gradient every video" problem.
 *
 * 8 distinct visual modes:
 *   gradient-drift   — Slow-morphing radial gradient blobs (default, cinematic)
 *   floating-shapes  — Outlined circles + squares drifting (tech/modern)
 *   grid-dots        — Animated dot grid with accent glow (editorial/data)
 *   grid-lines       — Fine line grid with gradient wash (corporate)
 *   particles-dense  — Dense upward particles (emotional/dramatic)
 *   dark-void        — Nearly black with subtle light volume (minimal/serious)
 *   warm-glow        — Warm amber gradients (personal/story)
 *   split-tone       — Two-tone diagonal split (before-after/contrast)
 */

export type BgMode =
  | "gradient-drift"
  | "floating-shapes"
  | "grid-dots"
  | "grid-lines"
  | "particles-dense"
  | "dark-void"
  | "warm-glow"
  | "split-tone";

export interface SceneBackgroundProps {
  mode: BgMode;
  /** Primary accent color */
  accent?: string;
  /** Secondary accent */
  accent2?: string;
  /** Base dark color */
  base?: string;
  /** Include film grain overlay */
  grain?: boolean;
  /** Include vignette */
  vignette?: boolean;
  /** Include noise texture */
  noise?: boolean;
  /** Unique seed for deterministic randomness */
  seed?: string;
}

export const SceneBackground: React.FC<SceneBackgroundProps> = ({
  mode,
  accent = "#2dd4bf",
  accent2 = "#f5ad43",
  base = "#000",
  grain = true,
  vignette = true,
  noise = true,
  seed = "bg",
}) => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{backgroundColor: base, overflow: "hidden"}}>
      {mode === "gradient-drift" && <GradientDrift frame={frame} accent={accent} accent2={accent2} />}
      {mode === "floating-shapes" && <FloatingShapes frame={frame} accent={accent} accent2={accent2} seed={seed} />}
      {mode === "grid-dots" && <GridDots frame={frame} accent={accent} />}
      {mode === "grid-lines" && <GridLines frame={frame} accent={accent} />}
      {mode === "particles-dense" && <ParticlesDense frame={frame} accent={accent} seed={seed} />}
      {mode === "dark-void" && <DarkVoid frame={frame} accent={accent} />}
      {mode === "warm-glow" && <WarmGlow frame={frame} />}
      {mode === "split-tone" && <SplitTone frame={frame} accent={accent} accent2={accent2} />}

      {noise && <Noise opacity={0.025} />}
      {grain && <FilmGrain opacity={0.04} />}
      {vignette && <Vignette intensity={0.65} />}
    </AbsoluteFill>
  );
};

// ─── GRADIENT DRIFT — morphing blobs ─────────────────────────────────
const GradientDrift: React.FC<{frame: number; accent: string; accent2: string}> = ({frame, accent, accent2}) => {
  const x1 = 20 + Math.sin(frame * 0.003) * 20;
  const y1 = 30 + Math.cos(frame * 0.004) * 15;
  const x2 = 80 + Math.sin(frame * 0.002 + 1) * 15;
  const y2 = 70 + Math.cos(frame * 0.003 + 2) * 20;
  const scale1 = 1 + Math.sin(frame * 0.004) * 0.15;
  const scale2 = 1 + Math.cos(frame * 0.003) * 0.12;

  return (
    <>
      <div style={{
        position: "absolute", left: `${x1}%`, top: `${y1}%`,
        width: 700, height: 700, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`,
        transform: `translate(-50%, -50%) scale(${scale1})`,
      }} />
      <div style={{
        position: "absolute", left: `${x2}%`, top: `${y2}%`,
        width: 500, height: 500, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent2}12 0%, transparent 70%)`,
        transform: `translate(-50%, -50%) scale(${scale2})`,
      }} />
    </>
  );
};

// ─── FLOATING SHAPES — outlined circles + squares ────────────────────
const FloatingShapes: React.FC<{frame: number; accent: string; accent2: string; seed: string}> = ({frame, accent, accent2, seed}) => {
  const shapes = Array.from({length: 10}, (_, i) => {
    const baseX = random(`${seed}-sx-${i}`) * 1080;
    const baseY = random(`${seed}-sy-${i}`) * 1920;
    const size = 60 + random(`${seed}-ss-${i}`) * 120;
    const speed = 0.2 + random(`${seed}-sp-${i}`) * 0.4;
    const phase = random(`${seed}-ph-${i}`) * Math.PI * 2;
    const isCircle = random(`${seed}-ic-${i}`) > 0.5;

    const x = baseX + Math.sin(frame * speed * 0.015 + phase) * 40;
    const y = baseY + Math.cos(frame * speed * 0.012 + phase) * 30;
    const rotation = frame * speed * 0.3 + i * 36;
    const opacity = 0.04 + random(`${seed}-op-${i}`) * 0.04;
    const color = i % 2 === 0 ? accent : accent2;

    return {x, y, size, rotation, opacity, isCircle, color};
  });

  return (
    <>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)
        `,
        backgroundSize: "80px 80px",
      }} />
      {/* Gradient wash */}
      <div style={{
        position: "absolute", inset: 0,
        background: `
          radial-gradient(ellipse at 25% 30%, ${accent}10 0%, transparent 50%),
          radial-gradient(ellipse at 75% 70%, ${accent2}08 0%, transparent 50%)
        `,
      }} />
      {/* Shapes */}
      {shapes.map((s, i) => (
        <div key={i} style={{
          position: "absolute", left: s.x, top: s.y,
          width: s.size, height: s.size,
          borderRadius: s.isCircle ? "50%" : "18%",
          border: `1px solid ${s.color}`,
          opacity: s.opacity,
          transform: `rotate(${s.rotation}deg)`,
        }} />
      ))}
    </>
  );
};

// ─── GRID DOTS — animated dot pattern ────────────────────────────────
const GridDots: React.FC<{frame: number; accent: string}> = ({frame, accent}) => {
  const offsetY = (frame * 0.3) % 40;
  return (
    <>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `radial-gradient(circle, ${accent}20 1.5px, transparent 1.5px)`,
        backgroundSize: "40px 40px",
        backgroundPosition: `0 ${offsetY}px`,
        opacity: 0.4,
      }} />
      <div style={{
        position: "absolute", left: "50%", top: "40%",
        width: 600, height: 600, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}15 0%, transparent 60%)`,
        transform: "translate(-50%, -50%)",
      }} />
    </>
  );
};

// ─── GRID LINES — fine lines with glow ───────────────────────────────
const GridLines: React.FC<{frame: number; accent: string}> = ({frame, accent}) => {
  const pulse = 0.015 + Math.sin(frame * 0.03) * 0.005;
  return (
    <>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,${pulse}) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,${pulse}) 1px, transparent 1px)
        `,
        backgroundSize: "60px 60px",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse at 50% 50%, ${accent}0c 0%, transparent 60%)`,
      }} />
    </>
  );
};

// ─── PARTICLES DENSE — many particles upward ─────────────────────────
const ParticlesDense: React.FC<{frame: number; accent: string; seed: string}> = ({frame, accent, seed}) => (
  <>
    <div style={{
      position: "absolute", inset: 0,
      background: `radial-gradient(ellipse at 50% 60%, ${accent}0a 0%, transparent 70%)`,
    }} />
    {Array.from({length: 50}, (_, i) => {
      const x = random(`${seed}-dpx-${i}`) * 1080;
      const baseY = random(`${seed}-dpy-${i}`) * 2200;
      const size = 1.5 + random(`${seed}-dps-${i}`) * 3;
      const speed = 0.3 + random(`${seed}-dsp-${i}`) * 0.6;
      const opacity = 0.05 + random(`${seed}-dop-${i}`) * 0.15;
      const y = ((baseY - frame * speed) % 2200 + 2200) % 2200 - 200;
      return (
        <div key={i} style={{
          position: "absolute", left: x, top: y,
          width: size, height: size, borderRadius: "50%",
          background: accent, opacity,
        }} />
      );
    })}
  </>
);

// ─── DARK VOID — near black with subtle light ────────────────────────
const DarkVoid: React.FC<{frame: number; accent: string}> = ({frame, accent}) => {
  const breathe = 0.03 + Math.sin(frame * 0.02) * 0.015;
  return (
    <div style={{
      position: "absolute", inset: 0,
      background: `radial-gradient(ellipse at 50% 40%, ${accent}${Math.round(breathe * 255).toString(16).padStart(2, "0")} 0%, transparent 50%)`,
    }} />
  );
};

// ─── WARM GLOW — amber/gold tones ────────────────────────────────────
const WarmGlow: React.FC<{frame: number}> = ({frame}) => {
  const x = 40 + Math.sin(frame * 0.003) * 15;
  const y = 35 + Math.cos(frame * 0.004) * 10;
  return (
    <>
      <div style={{
        position: "absolute", left: `${x}%`, top: `${y}%`,
        width: 800, height: 800, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(245,173,67,0.10) 0%, transparent 60%)",
        transform: "translate(-50%, -50%)",
      }} />
      <div style={{
        position: "absolute", left: "65%", top: "60%",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(248,113,113,0.06) 0%, transparent 60%)",
        transform: "translate(-50%, -50%)",
      }} />
    </>
  );
};

// ─── SPLIT TONE — diagonal two-color ─────────────────────────────────
const SplitTone: React.FC<{frame: number; accent: string; accent2: string}> = ({frame, accent, accent2}) => {
  const angle = 135 + Math.sin(frame * 0.002) * 5;
  return (
    <div style={{
      position: "absolute", inset: 0,
      background: `linear-gradient(${angle}deg, ${accent}12 0%, transparent 50%, ${accent2}08 100%)`,
    }} />
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
